import React, { Component, type ReactNode, type ErrorInfo } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[ErrorBoundary]", error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? <DefaultFallback />;
    }
    return this.props.children;
  }
}

function DefaultFallback() {
  return (
    <div className="flex flex-col justify-center items-center h-full min-h-[200px] bg-bg-subtle select-none p-8">
      <p className="text-base text-fg-medium">Something went wrong</p>
      <button
        onClick={() => window.location.reload()}
        className="mt-4 px-4 py-2 text-xs font-medium text-fg-bold bg-bg border border-border rounded-sm hover:bg-bg-hover transition-colors duration-100 cursor-pointer"
      >
        Reload page
      </button>
    </div>
  );
}
